import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { portfolioImages } from '../mockData';
import { PageHero, Reveal } from '../components/ui-kit';

const categories = ['Tous', 'Électricité', 'Plâtrerie', 'Rénovation'];
const slug = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const Lightbox = ({ images, index, onClose, onMove }) => {
  const img = images[index];
  return (
    <div className="fixed inset-0 z-[100] bg-abyss/95 backdrop-blur-xl flex items-center justify-center p-4" onClick={onClose} data-testid="gallery-lightbox">
      <button className="absolute top-5 right-5 w-11 h-11 rounded-xl bg-white/10 hover:bg-energy text-white flex items-center justify-center transition-colors" onClick={onClose} aria-label="Fermer" data-testid="lightbox-close">
        <X size={20} />
      </button>
      <button className="absolute left-4 sm:left-8 w-11 h-11 rounded-xl bg-white/10 hover:bg-energy text-white flex items-center justify-center transition-colors" onClick={(e) => { e.stopPropagation(); onMove(-1); }} aria-label="Précédent" data-testid="lightbox-prev">
        <ChevronLeft size={22} />
      </button>
      <button className="absolute right-4 sm:right-8 w-11 h-11 rounded-xl bg-white/10 hover:bg-energy text-white flex items-center justify-center transition-colors" onClick={(e) => { e.stopPropagation(); onMove(1); }} aria-label="Suivant" data-testid="lightbox-next">
        <ChevronRight size={22} />
      </button>
      <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={img.url} alt={img.title} className="w-full max-h-[78vh] object-contain rounded-2xl border border-white/10" />
        <figcaption className="mt-4 flex items-center justify-between text-white">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">{img.category}</span>
            <p className="font-display font-semibold mt-1">{img.title}</p>
          </div>
          <span className="font-mono text-xs text-slate-400">{index + 1} / {images.length}</span>
        </figcaption>
      </figure>
    </div>
  );
};

const Portfolio = () => {
  const [active, setActive] = useState('Tous');
  const [open, setOpen] = useState(null);

  const filtered = active === 'Tous' ? portfolioImages : portfolioImages.filter((i) => i.category === active);
  const count = (c) => (c === 'Tous' ? portfolioImages.length : portfolioImages.filter((i) => i.category === c).length);

  return (
    <div className="min-h-screen bg-abyss" data-testid="portfolio-page">
      <PageHero
        testId="portfolio-hero"
        eyebrow="Nos réalisations"
        title={<>Galerie <span className="text-energy-gradient">Photo</span></>}
        subtitle="Découvrez nos réalisations en électricité et plâtrerie : tableaux, éclairages architecturaux, cloisons et plafonds."
        image="/assets/projet17.jpg"
      />

      <section className="relative pb-28 text-white z-10" data-testid="portfolio-gallery">
        <div className="absolute inset-0 circuit-grid opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap gap-3 pt-12 mb-12" data-testid="gallery-filters">
            {categories.map((c) => {
              const isActive = active === c;
              return (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  data-testid={`gallery-tab-${slug(c)}`}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-all duration-300 ${
                    isActive
                      ? 'bg-energy border-energy text-white shadow-[0_0_20px_rgba(255,94,20,0.45)]'
                      : 'bg-white/5 border-white/15 text-slate-300 hover:border-white/40 hover:text-white'
                  }`}
                >
                  {c}
                  <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${isActive ? 'bg-white/20' : 'bg-white/10'}`}>{count(c)}</span>
                </button>
              );
            })}
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="gallery-grid">
            {filtered.map((img, i) => (
              <Reveal key={img.id} delay={(i % 6) * 60}>
                <button
                  onClick={() => setOpen(i)}
                  data-testid={`gallery-item-${img.id}`}
                  className="group relative block w-full aspect-[4/5] sm:aspect-square rounded-2xl overflow-hidden border border-white/10 hover:border-energy/60 transition-colors brackets text-left"
                >
                  <img src={img.url} alt={img.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-abyss/95 via-abyss/30 to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Maximize2 size={15} />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 translate-y-3 group-hover:translate-y-0 transition-transform duration-500">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber">{img.category}</span>
                    <p className="text-sm sm:text-base font-semibold mt-1 line-clamp-2">{img.title}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-slate-400 py-20" data-testid="gallery-empty">Aucune réalisation dans cette catégorie pour le moment.</p>
          )}
        </div>
      </section>

      {open !== null && (
        <Lightbox
          images={filtered}
          index={open}
          onClose={() => setOpen(null)}
          onMove={(d) => setOpen((open + d + filtered.length) % filtered.length)}
        />
      )}
    </div>
  );
};

export default Portfolio;
